import { isEmpty } from "lodash";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { Grid } from "semantic-ui-react";
import { checkYearValidation } from "../../utils/utils";
import "./inputText.scss";
import { InputNumberTypes } from "./InputTypes";

export const InputNumber = forwardRef<HandleValidation, InputNumberTypes>((props, ref) => {
  const {
    computer = undefined,
    tablet = undefined,
    mobile = undefined,
    name,
    errors,
    control,
    transform,
    defaultValue,
    customLabelText,
    labelName,
    customGridMain,
    customGridColumn,
    errorMessage,
    customErrorText,
    customText,
    required = false,
    otherValidation,
    checkValidation = false,
    enableMinus = false,
    placeholder = "",
    customRequired, // This is want when we want view required message but user can submit it
    removeDecimal = false,
    rightContentEnable = false,
    rightContent = "",
    enableDecimalWithMinus = false,
    isYearInput = false,
    disabled = false,
    onChangeFunction = () => {},
    enableLimitedDecimal = false,
    viewDecimalNumber = 4,
    customOtherValidation,
    testId
  } = props;

  const [customValidation, setCustomValidation] = useState(false);
  const [customMessage, setCustomMessage] = useState(errorMessage);
  let errorViewStyle = "textInputFormError";
  let errorBottomStyle = "textInputError";

  const getCustomValidationResult = (value: any) => {
    if ((value === undefined || value === null || value === "" || value === 0) && customRequired) {
      return { valid: false, message: errorMessage };
    }

    if (customOtherValidation?.validate) {
      const validations = customOtherValidation.validate;

      for (const key in validations) {
        const validator = validations[key];
        const result = validator(value);

        if (result !== true) {
          return { valid: false, message: result };
        }
      }
    }

    return { valid: true, message: "" };
  };

  const customValidationCheck = (value: any) => {
    const validationResult = getCustomValidationResult(value);
    setCustomValidation(!validationResult.valid);
    setCustomMessage(validationResult.message);
    return validationResult.valid;
  };

  useImperativeHandle(ref, () => ({
    checkCustomRequired(data: any) {
      customValidationCheck(data);
    }
  }));

  //check error message
  const checkErrorMessage = (errors: any) => {
    if (isEmpty(errors)) return "";
    switch (errors.type) {
      case "required":
        return errorMessage;
      default:
        return errors.message;
    }
  };

  //check error message
  const checkError = (errors: any) => {
    if (errors) {
      errorViewStyle = "textInputFormError";
      errorBottomStyle = "textInputError";
      return true;
    } else if (customValidation) {
      errorViewStyle = "textInputFormCustomError";
      errorBottomStyle = "textInputCustomError";
      return true;
    } else {
      errorViewStyle = "textInputFormError";
      errorBottomStyle = "textInputError";
      return false;
    }
  };

  const getErrorMessage = ({
    customValidation,
    customRequired,
    customMessage,
    errors
  }: {
    customValidation: boolean;
    customRequired: boolean;
    customMessage: string;
    errors: any;
  }) => {
    // If custom validation fails and the field is required, show the custom message
    if (customValidation && customRequired) {
      return customMessage;
    }
    if (customValidation) {
      return customMessage;
    }
    return checkErrorMessage(errors);
  };

  const formatWithCommas = (value: string) => {
    if (value === null || value === undefined) return "";
    const strValue = String(value);
    const isNegative = strValue.startsWith("-");
    const cleanValue = strValue.replace(/[^0-9.]/g, "");
    const [integerPart, decimalPart] = cleanValue.split(".");
    const int = parseInt(integerPart);
    const formattedInt = isNaN(int) ? "" : integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    const formattedValue =
      decimalPart !== undefined ? `${formattedInt}.${decimalPart}` : formattedInt;

    return isNegative ? `-${formattedValue}` : formattedValue;
  };

  //check error number
  const checkNumber = () => {
    if (checkValidation) {
      return transform;
    } else {
      return {
        input: (value: any) => {
          value = String(value ?? "");
          if (isYearInput) {
            value = value.replace(/[^\d]/g, "");
            return value.slice(0, 4);
          }
          if (enableMinus) {
            value = enableDecimalWithMinus
              ? value.replace(/[^0-9.-]/g, "")
              : value.replace(/[^0-9-]/g, "");

            if (value.includes("-")) {
              const minusCount = (value.match(/-/g) || []).length;
              if (minusCount > 1) {
                value = value.replace(/-/g, (match: any, offset: any) => (offset === 0 ? "-" : ""));
              }
              if (value.indexOf("-") > 0) {
                value = value.replace("-", "");
                value = "-" + value;
              }
            }
            if (enableDecimalWithMinus) {
              const dotCount = (value.match(/\./g) || []).length;
              if (dotCount > 1) {
                const firstDotIndex = value.indexOf(".");
                value = value.replace(/\./g, (match: any, offset: any) =>
                  offset === firstDotIndex ? "." : ""
                );
              }
            }
            return value;
          } else {
            value = removeDecimal ? value.replace(/[^0-9]/g, "") : value.replace(/[^0-9.]/g, "");

            if (!removeDecimal) {
              // Allow only one dot
              const dotCount = (value.match(/\./g) || []).length;
              if (dotCount > 1) {
                const firstDotIndex = value.indexOf(".");
                value = value.replace(/\./g, (match: any, offset: any) =>
                  offset === firstDotIndex ? "." : ""
                );
              }
            }

            if (enableLimitedDecimal) {
              const [intPart, decimalPart] = value.split(".");
              if (decimalPart && decimalPart.length > viewDecimalNumber) {
                value = `${intPart}.${decimalPart.substring(0, viewDecimalNumber)}`;
              }
            }

            return value;
          }
        },
        output: (e: any) => {
          const value = e.target.value || "";
          if (enableMinus) {
            return value;
          } else {
            const cleanedNumber = value.replace(/[^0-9.]/g, "");
            return isNaN(parseFloat(cleanedNumber)) ? "" : cleanedNumber;
          }
        }
      };
    }
  };

  //check input change
  const inputOnchangeValue = (e: any, field: any) => {
    const raw = e.target.value.replace(/,/g, "");
    const value = checkValidation
      ? transform.output({ target: { value: raw } })
      : checkNumber().output({ target: { value: raw } });

    customValidationCheck(value);
    field.onChange(value);
    onChangeFunction(e);
  };

  //return input changes
  const returnValue = (field: any) => {
    const value = field?.value ?? defaultValue ?? "";
    const rawValue = checkValidation ? transform.input(value) : checkNumber().input(value);

    return isYearInput ? rawValue : formatWithCommas(rawValue);
  };

  // check year is required
  const isYearRequired = useMemo(() => {
    return isYearInput && required;
  }, [isYearInput, required]);
  return (
    <>
      <label
        data-testid={testId ? `${testId}-label` : undefined}
        className={`inputTextLable ${customLabelText}`}
      >
        {labelName}
      </label>
      <Grid className={`${customGridMain}`}>
        <Grid.Column
          computer={computer}
          tablet={tablet}
          mobile={mobile}
          className={`inputTextContanier ${customGridColumn}`}
        >
          <div className="inputWrapper">
            <Controller
              defaultValue={defaultValue}
              control={control}
              rules={{
                required: {
                  value: required,
                  message: errorMessage
                },
                ...(isYearRequired && {
                  validate: {
                    yearValid: (v: any) => {
                      const year = parseInt(v);
                      return checkYearValidation(year) || "Please enter a valid year";
                    }
                  }
                }),
                ...otherValidation
              }}
              name={name}
              render={({ field }) => (
                <input
                  className={
                    checkError(errors)
                      ? `${errorViewStyle} ${customText} ${
                          rightContentEnable ? "customInputDataView" : ""
                        }`
                      : `textInputFormText ${customText} ${
                          rightContentEnable ? "customInputDataView" : ""
                        }`
                  }
                  onChange={e => inputOnchangeValue(e, field)}
                  value={returnValue(field)}
                  placeholder={placeholder}
                  disabled={disabled}
                  data-testid={testId ? `${testId}-input` : undefined}
                />
              )}
            />
            {rightContentEnable && (
              <div className="inputRightContent">
                <p className="rightContentText">{rightContent}</p>
              </div>
            )}
          </div>

          {checkError(errors) && (
            <span
              data-testid={testId ? `${testId}-error` : undefined}
              className={`${errorBottomStyle} ${customErrorText}`}
            >
              {getErrorMessage({
                customValidation,
                customRequired,
                customMessage,
                errors
              })}
            </span>
          )}
        </Grid.Column>
      </Grid>
    </>
  );
});
